import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '../components/ui/carousel';
import { 
  Building2, 
  Users, 
  ClipboardCheck, 
  FileText, 
  Bell,
  Shield,
  ArrowRight,
  CheckCircle,
  BarChart3,
  Calendar,
  Building,
  Clock,
  Zap,
  Star,
  ChevronDown,
  Play,
  TrendingUp,
  Target,
  Award,
  Lock,
  X,
  Wrench,
  MessageCircle,
  Download,
  Headphones,
  CreditCard,
  ShieldCheck,
  BadgeCheck,
  Globe,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX,
  Pause
} from 'lucide-react';

// Video Card Component with Play/Pause and Audio controls
const VideoCard = ({ src, icon: Icon, iconBg, tagColor, tag, title, subtitle }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.muted = false;
        setIsMuted(false);
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div 
      className="group relative rounded-2xl overflow-hidden bg-card shadow-lg hover:shadow-xl transition-all cursor-pointer"
      onClick={togglePlay}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => !isPlaying && setShowControls(true)}
    >
      <video 
        ref={videoRef}
        className="w-full aspect-video object-cover"
        loop 
        muted={isMuted}
        playsInline
        poster=""
      >
        <source src={src} type="video/mp4" />
      </video>
      
      {/* Play Button Overlay */}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}>
        <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg transform transition-transform hover:scale-110">
          {isPlaying ? (
            <Pause className="w-7 h-7 text-gray-800" />
          ) : (
            <Play className="w-7 h-7 text-gray-800 ml-1" />
          )}
        </div>
      </div>

      {/* Mute/Unmute Button */}
      {isPlaying && (
        <button
          onClick={toggleMute}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors z-10"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      )}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none"></div>
      
      {/* Bottom Info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white pointer-events-none">
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-6 h-6 rounded-full ${iconBg} flex items-center justify-center`}>
            <Icon className="w-3 h-3" />
          </div>
          <span className={`text-xs font-medium ${tagColor}`}>{tag}</span>
        </div>
        <h3 className="text-lg font-bold">{title}</h3>
        <p className="text-xs text-white/80">{subtitle}</p>
      </div>
    </div>
  );
};

const features = [
  {
    icon: Building2,
    title: 'Property & Unit Management',
    description: 'Add unlimited properties and units. Track occupancy, rent amounts, and unit details all in one place.',
    benefit: 'Save 5+ hours/week on admin'
  },
  {
    icon: Users,
    title: 'Tenant Tracking',
    description: 'Complete tenant profiles with lease dates, contact info, and payment status. Never miss a lease renewal.',
    benefit: 'Zero missed renewals'
  },
  {
    icon: ClipboardCheck,
    title: 'Inspection Workflows',
    description: 'Schedule inspections, assign to team members, track completion status, and maintain compliance records.',
    benefit: '100% inspection compliance'
  },
  {
    icon: FileText,
    title: 'Document Storage',
    description: 'Upload leases, IDs, inspection reports. Everything organized and linked to the right tenant or property.',
    benefit: 'Find any doc in seconds'
  },
  {
    icon: Wrench,
    title: 'Maintenance Requests',
    description: 'Track and manage work orders from submission to completion. Assign tasks, set priorities, and monitor progress.',
    benefit: 'Faster response times'
  },
  {
    icon: Bell,
    title: 'Smart Notifications',
    description: 'Get alerts for lease expirations, upcoming inspections, and team activities. Never miss a deadline.',
    benefit: 'Stay ahead of deadlines'
  },
  {
    icon: Shield,
    title: 'Team Access Control',
    description: 'Invite your team with Admin, Manager, or Staff roles. Control exactly who can view and edit what.',
    benefit: 'Secure & organized'
  },
  {
    icon: MessageCircle,
    title: 'Tenant Portal',
    description: 'Give tenants a dedicated portal to submit requests, upload documents, and communicate securely.',
    benefit: 'Happy tenants, less calls'
  }
];

const testimonials = [
  {
    quote: "We went from juggling 5 different spreadsheets to having everything in one place. Our team saves at least 10 hours per week.",
    author: "Sarah M.",
    role: "Property Manager",
    company: "Horizon Properties",
    units: "45 units"
  },
  {
    quote: "The inspection tracking alone is worth it. We've never been more compliant, and our audit prep time dropped by 80%.",
    author: "Michael R.",
    role: "Operations Director",
    company: "Urban Living Co",
    units: "120 units"
  },
  {
    quote: "Finally, a tool that understands property management. Setup took 15 minutes and my team was productive on day one.",
    author: "Jennifer K.",
    role: "Portfolio Manager",
    company: "Coastal Rentals",
    units: "78 units"
  }
];

const stats = [
  { value: '10+', label: 'Hours saved per week', icon: Clock },
  { value: '100%', label: 'Inspection compliance', icon: Target },
  { value: '15 min', label: 'Average setup time', icon: Zap }
];

const pricingPlans = {
  monthly: {
    free: { price: 0, period: '/month' },
    standard: { price: 99, period: '/month' },
    pro: { price: 249, period: '/month' },
    enterprise: { price: 599, period: '/month' }
  },
  annual: {
    free: { price: 0, period: '/year' },
    standard: { price: 990, period: '/year', savings: 'Save $198/year' },
    pro: { price: 2490, period: '/year', savings: 'Save $498/year' },
    enterprise: { price: 5990, period: '/year', savings: 'Save $1,198/year' }
  }
};

const planFeatures = {
  free: [
    'Up to 2 properties',
    'Up to 5 units'
  ],
  standard: [
    'Up to 20 properties',
    'Up to 40 units',
    'Full inspection workflows',
    'Tenant Portal with photo uploads',
    'Contractor Portal access',
    '10GB document storage',
    'Email notifications',
    'Exportable reports (CSV/PDF)',
    '5 team members'
  ],
  pro: [
    'Unlimited properties',
    'Unlimited units',
    'Unlimited team members',
    'AI-Powered Insights Dashboard',
    'REST API access with key management',
    'Two-factor authentication (2FA)',
    'Full audit logs',
    '100GB document storage',
    'Priority support'
  ],
  enterprise: [
    'Everything in Pro',
    'Bulk audit export',
    'Single Sign-On (SSO)',
    'Multi-organization controls',
    'Dedicated onboarding',
    'SLA-backed support'
  ]
};

const faqs = [
  {
    q: "How long is the free plan available?",
    a: "The free plan is available forever, not a trial. You can use MyPropOps completely free for up to 2 properties and 5 units with no time limit. When you're ready to scale, upgrade to Standard or Pro."
  },
  {
    q: "Do I need a credit card to start?",
    a: "No credit card required. Sign up with just your email and start using MyPropOps immediately. We'll only ask for payment info if you choose to upgrade."
  },
  {
    q: "What's the difference between monthly and annual billing?",
    a: "Annual billing saves you up to 17% compared to monthly billing. You're billed once per year at the discounted rate. You can switch between billing periods at any time."
  },
  {
    q: "Can I upgrade or downgrade at any time?",
    a: "Yes! You can upgrade instantly and get immediate access to new features. Downgrades take effect at the end of your current billing period. No penalties or fees."
  },
  {
    q: "Is my data secure?",
    a: "Absolutely. We use industry-standard AES-256 encryption, secure cloud infrastructure on AWS, and role-based access controls. Your data is backed up daily across multiple regions."
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit cards (Visa, Mastercard, American Express), as well as ACH bank transfers for annual plans. Enterprise customers can pay via invoice."
  }
];

const INFOGRAPHIC_URL = "https://static.prod-images.emergentagent.com/jobs/ff35c095-fb57-42ce-8736-9575eebfd0f0/images/8d3cdb0f84cc5c5fc4bb51faed874a21a964e83906fc272099fbb18c77725831.png";

const Landing = () => {
  const [openFaq, setOpenFaq] = React.useState(null);
  const [billingPeriod, setBillingPeriod] = React.useState('annual');

  return (
    <div className="min-h-screen bg-background">
      {/* Skip to main content for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md">
        Skip to main content
      </a>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo-small.jpg" 
                alt="MyPropOps"
                width="40"
                height="40"
                className="w-10 h-10 rounded-lg object-contain"
              />
              <span className="text-xl font-bold font-heading">MyPropOps</span>
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</a>
              <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
              <a href="#testimonials" className="text-muted-foreground hover:text-foreground transition-colors">Reviews</a>
              <a href="#faq" className="text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
              <Link to="/help" className="text-muted-foreground hover:text-foreground transition-colors">Help Center</Link>
              <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
              <Link to="/tenant-portal/login" className="text-emerald-600 hover:text-emerald-700 transition-colors font-medium">For Tenants</Link>
              <Link to="/contractor/login" className="text-orange-600 hover:text-orange-700 transition-colors font-medium">For Contractors</Link>
              <Link to="/owner/login" className="text-blue-600 hover:text-blue-700 transition-colors font-medium">For Owners</Link>
            </div>
            <div className="hidden sm:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button data-testid="nav-get-started">Start Free</Button>
              </Link>
            </div>
            {/* Mobile: Just show Sign In and Start Free */}
            <div className="flex sm:hidden items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button size="sm">Start Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Bar - Fixed at bottom on mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-card border-t border-border safe-bottom">
        <div className="flex items-center justify-around h-14">
          <a href="#features" className="flex flex-col items-center justify-center flex-1 h-full text-muted-foreground hover:text-foreground">
            <span className="text-xs">Features</span>
          </a>
          <a href="#pricing" className="flex flex-col items-center justify-center flex-1 h-full text-muted-foreground hover:text-foreground">
            <span className="text-xs">Pricing</span>
          </a>
          <Link to="/tenant-portal/login" className="flex flex-col items-center justify-center flex-1 h-full text-emerald-600 hover:text-emerald-700">
            <span className="text-xs font-medium">Tenants</span>
          </Link>
          <Link to="/owner/login" className="flex flex-col items-center justify-center flex-1 h-full text-blue-600 hover:text-blue-700">
            <span className="text-xs font-medium">Owners</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <main id="main-content">
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Award className="w-4 h-4" />
                Compliance-Centered Property Operations
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading leading-tight">
                Stop drowning in spreadsheets.<br />
                <span className="text-primary">Start managing.</span>
              </h1>
              <p className="mt-6 text-xl text-muted-foreground max-w-xl">
                MyPropOps is compliance-centered property operations software for landlords, property managers, and housing teams — inspections, maintenance, documents, and tenant workflows in one accountable system, built for HUD audits and Section 8 documentation requirements.
              </p>
              
              {/* Value Props */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Setup in 15 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm">No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Free up to 5 units</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm">Cancel anytime</span>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto btn-active text-base px-8" data-testid="hero-get-started">
                    Start My Free Account
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-base">
                    <Play className="w-4 h-4 mr-2" />
                    See How It Works
                  </Button>
                </a>
              </div>
              
              <p className="mt-4 text-sm text-muted-foreground">
                Built for compliance-first property operations
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-primary/5 rounded-3xl blur-3xl" />
              <Card className="relative glass overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-8">
                    <div className="bg-card rounded-xl p-4 shadow-lg mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium">Dashboard Overview</span>
                        <span className="text-xs text-green-500 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Live
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-muted/50 rounded-lg p-3">
                          <BarChart3 className="w-6 h-6 text-primary mb-1" />
                          <p className="text-xl font-bold">24</p>
                          <p className="text-xs text-muted-foreground">Properties</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3">
                          <Users className="w-6 h-6 text-primary mb-1" />
                          <p className="text-xl font-bold">156</p>
                          <p className="text-xs text-muted-foreground">Tenants</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3">
                          <ClipboardCheck className="w-6 h-6 text-green-500 mb-1" />
                          <p className="text-xl font-bold">98%</p>
                          <p className="text-xs text-muted-foreground">Compliance</p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3">
                          <Calendar className="w-6 h-6 text-amber-500 mb-1" />
                          <p className="text-xl font-bold">3</p>
                          <p className="text-xs text-muted-foreground">Due This Week</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-card rounded-xl p-3 shadow-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-sm">Inspection completed: Unit 4B - Oak Apartments</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 mx-auto text-primary mb-2" />
                <p className="text-3xl font-bold font-heading">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-card border-b" data-testid="trust-signals">
        <div className="max-w-7xl mx-auto">
          {/* Security & Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-950/30 rounded-full border border-green-200 dark:border-green-800">
              <ShieldCheck className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Bank-Grade 256-bit Encryption</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-950/30 rounded-full border border-blue-200 dark:border-blue-800">
              <Lock className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">SSL Secured</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-950/30 rounded-full border border-purple-200 dark:border-purple-800">
              <Headphones className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">U.S. Based Support</span>
            </div>
          </div>

          {/* Powered By / Integrations */}
          <div className="text-center mb-6">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4">Powered by enterprise-grade infrastructure</p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all">
              {/* Stripe */}
              <div className="flex items-center gap-2">
                <svg className="h-8 w-16" viewBox="0 0 60 25" fill="currentColor" aria-label="Stripe">
                  <path d="M5 10.1c0-.6.6-1 1.3-1 1.2 0 2.7.4 3.9 1.1V6.7c-1.3-.5-2.6-.8-3.9-.8C3.2 5.9 1 7.6 1 10.3c0 4 5.5 3.4 5.5 5.1 0 .7-.6 1-1.4 1-1.2 0-2.8-.5-4.1-1.2v3.6c1.4.6 2.8.9 4.1.9 3.2 0 5.4-1.6 5.4-4.3C10.5 11.1 5 11.8 5 10.1z"/>
                  <path d="M19.5 5.9l-3.3.7v3.5h-1.7v3.1h1.7v4.3c0 2.7 1.3 3.7 3.6 3.7.8 0 1.6-.2 2.3-.4v-3c-.4.1-.8.2-1.3.2-.9 0-1.3-.4-1.3-1.3v-3.5h2.6V10h-2.6V5.9z"/>
                  <path d="M27.4 8.5l-.2-1.6h-3.5v14h4V12.3c.9-1.2 2.4-1 2.9-.8V7.9c-.5-.2-2.3-.5-3.2 1z"/>
                  <path d="M33.5 6.9h4v14h-4z"/>
                  <path d="M33.5 3.4l4-.8v3h-4z"/>
                  <path d="M45.2 6.4c-1.5 0-2.4.7-3 1.2l-.2-1h-3.5v17.8l4-.8V21c.6.4 1.5.8 2.6.8 2.6 0 5-2.1 5-6.7.1-4.2-2.4-6.6-4.9-6.7zm-.9 10.3c-.9 0-1.4-.3-1.8-.7v-5.5c.4-.5.9-.7 1.8-.7 1.4 0 2.3 1.5 2.3 3.4 0 2-1 3.5-2.3 3.5z"/>
                  <path d="M56.3 6.4c-2.8 0-4.7 2.4-4.7 6.7 0 4.4 2.1 6.6 5.7 6.6 1.6 0 2.9-.4 3.8-.9V16c-.8.4-1.8.6-2.9.6-1.2 0-2.2-.4-2.3-1.8h5.8c0-.2 0-.8 0-1.1 0-4.5-2.1-6.7-5.4-6.7zM54 12c0-1.3.8-1.9 1.7-1.9s1.6.6 1.6 1.9H54z"/>
                </svg>
                <span className="text-sm font-medium">Stripe</span>
              </div>
              {/* AWS */}
              <div className="flex items-center gap-2">
                <Globe className="w-6 h-6" />
                <span className="text-sm font-medium">Cloud Hosted</span>
              </div>
              {/* MongoDB */}
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6" />
                <span className="text-sm font-medium">Enterprise Database</span>
              </div>
            </div>
          </div>

          {/* Guarantees */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">No Hidden Fees</p>
                <p className="text-xs text-muted-foreground">Clear pricing always</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Export Anytime</p>
                <p className="text-xs text-muted-foreground">Your data is yours</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <BadgeCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">No Unit Minimums</p>
                <p className="text-xs text-muted-foreground">Start with 1 property</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Headphones className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Human Support</p>
                <p className="text-xs text-muted-foreground">Real people, real help</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-muted/30" data-testid="video-showcase">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Play className="w-4 h-4" />
              See It In Action
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              Property Management, Simplified
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              Click to play with sound and see how MyPropOps transforms property management
            </p>
          </div>

          {/* Video Carousel */}
          <div className="px-12">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {/* Video 1: Spreadsheet Killer */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/2">
                  <VideoCard
                    src="https://customer-assets.emergentagent.com/job_9fe5d8d9-7e94-4b0b-a436-c83b4b94ab4a/artifacts/j0e4chjv_prop5.mp4"
                    icon={X}
                    iconBg="bg-red-500"
                    tagColor="text-red-300"
                    tag="No More Spreadsheets"
                    title="Spreadsheet Killer"
                    subtitle="Ditch the spreadsheets. Get the dashboard."
                  />
                </CarouselItem>

                {/* Video 2: Compliance Shield */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/2">
                  <VideoCard
                    src="https://customer-assets.emergentagent.com/job_9fe5d8d9-7e94-4b0b-a436-c83b4b94ab4a/artifacts/fzrlnm2b_prop3.mp4"
                    icon={Shield}
                    iconBg="bg-blue-500"
                    tagColor="text-blue-300"
                    tag="100% Compliant"
                    title="Compliance Shield"
                    subtitle="Compliance deadlines? Already handled."
                  />
                </CarouselItem>

                {/* Video 3: Maintenance Magic */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/2">
                  <VideoCard
                    src="https://customer-assets.emergentagent.com/job_9fe5d8d9-7e94-4b0b-a436-c83b4b94ab4a/artifacts/lf1xwx6w_prop2.mp4"
                    icon={Wrench}
                    iconBg="bg-orange-500"
                    tagColor="text-orange-300"
                    tag="One-Tap Fixes"
                    title="Maintenance Magic"
                    subtitle="Click. Assigned. Done."
                  />
                </CarouselItem>

                {/* Video 4: Whole Team */}
                <CarouselItem className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/2">
                  <VideoCard
                    src="https://customer-assets.emergentagent.com/job_9fe5d8d9-7e94-4b0b-a436-c83b4b94ab4a/artifacts/fyzgszf2_prop1.mp4"
                    icon={Users}
                    iconBg="bg-purple-500"
                    tagColor="text-purple-300"
                    tag="Team Sync"
                    title="Whole Team"
                    subtitle="Get your whole team on the same page."
                  />
                </CarouselItem>
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-6 bg-background/80 backdrop-blur-sm border-border hover:bg-background" />
              <CarouselNext className="hidden md:flex -right-6 bg-background/80 backdrop-blur-sm border-border hover:bg-background" />
            </Carousel>
          </div>

          {/* CTA Below Carousel */}
          <div className="mt-10 text-center">
            <Link to="/register">
              <Button size="lg" className="font-semibold">
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground mt-2">Built for property managers who need accountability</p>
          </div>
        </div>
      </section>

      {/* Portal Showcase Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="portal-showcase">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Users className="w-4 h-4" />
              Dedicated Portals
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              One platform. Every stakeholder.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-lg">
              Give tenants, contractors, and property owners their own secure access points with dedicated features
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Tenant Portal */}
            <Card className="glass card-hover group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Users className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Tenant Portal</h3>
                <p className="text-muted-foreground mb-6">
                  Tenants can submit maintenance requests, pay rent, and access documents
                </p>
                <Link to="/tenant-portal/login">
                  <Button variant="outline" className="w-full text-emerald-600 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700">
                    Access Portal
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Owner Portal */}
            <Card className="glass card-hover group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Building className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Owner Portal</h3>
                <p className="text-muted-foreground mb-6">
                  Property investors track performance, occupancy, and revenue in real-time
                </p>
                <Link to="/owner/login">
                  <Button variant="outline" className="w-full text-blue-600 border-blue-200 hover:bg-blue-100 hover:text-blue-700">
                    Access Portal
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Contractor Portal */}
            <Card className="glass card-hover group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Wrench className="w-10 h-10 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Contractor Portal</h3>
                <p className="text-muted-foreground mb-6">
                  Contractors manage work orders, update job status, and message managers
                </p>
                <Link to="/contractor/login">
                  <Button variant="outline" className="w-full text-orange-600 border-orange-200 hover:bg-orange-100 hover:text-orange-700">
                    Access Portal
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Financial Infographic Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="infographic-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              See your portfolio at a glance
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Real-time analytics and insights to help you make smarter property decisions
            </p>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-primary/10 rounded-3xl blur-2xl" />
            <Card className="relative glass overflow-hidden">
              <CardContent className="p-4 sm:p-8">
                <img 
                  src={INFOGRAPHIC_URL} 
                  alt="MyPropOps Financial Analytics Dashboard showing property metrics, occupancy rates, and revenue insights"
                  className="w-full h-auto rounded-xl shadow-lg"
                  width="1280"
                  height="896"
                  loading="lazy"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="problem-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              Sound familiar?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Problems */}
            <Card className="glass border-destructive/30 bg-destructive/5">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-6 text-destructive flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-destructive/20 flex items-center justify-center text-sm">1</span>
                  The old way
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">✕</span>
                    <div>
                      <p className="font-medium">Spreadsheet chaos</p>
                      <p className="text-sm text-muted-foreground">Multiple files, outdated info, no single source of truth</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">✕</span>
                    <div>
                      <p className="font-medium">Missed inspections</p>
                      <p className="text-sm text-muted-foreground">No reminders, compliance risk, scrambling at audit time</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">✕</span>
                    <div>
                      <p className="font-medium">Team confusion</p>
                      <p className="text-sm text-muted-foreground">No visibility into who's doing what, duplicate work</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">✕</span>
                    <div>
                      <p className="font-medium">Lost documents</p>
                      <p className="text-sm text-muted-foreground">Leases in email, IDs in folders, nothing linked</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
            {/* Solutions */}
            <Card className="glass border-primary/30 bg-primary/5">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-6 text-primary flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm">2</span>
                  The MyPropOps way
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">One dashboard</p>
                      <p className="text-sm text-muted-foreground">All properties, tenants, and data in one place</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Automated reminders</p>
                      <p className="text-sm text-muted-foreground">Never miss an inspection or lease renewal again</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Team coordination</p>
                      <p className="text-sm text-muted-foreground">Assign tasks, track progress, complete audit trail</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Organized documents</p>
                      <p className="text-sm text-muted-foreground">Everything linked to the right tenant and property</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              Get started in 3 simple steps
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Most teams are fully operational in under 15 minutes
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Create your account</h3>
              <p className="text-muted-foreground">
                Sign up free in 30 seconds. No credit card, no commitment.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Add your properties</h3>
              <p className="text-muted-foreground">
                Enter your properties and units. We'll auto-create unit records for you.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Invite your team</h3>
              <p className="text-muted-foreground">
                Add team members with the right access levels. Start collaborating immediately.
              </p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/register">
              <Button size="lg" className="btn-active">
                Start My Free Account
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              Everything you need. Nothing you don't.
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Built specifically for property managers—not a generic tool adapted for real estate
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="glass card-hover group" data-testid={`feature-card-${i}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold font-heading mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground mb-3">{feature.description}</p>
                  <p className="text-sm text-primary font-medium flex items-center gap-1">
                    <Award className="w-4 h-4" />
                    {feature.benefit}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              What property managers say
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              See why teams are switching to MyPropOps
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="glass">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-semibold text-primary">{t.author[0]}</span>
                    </div>
                    <div>
                      <p className="font-semibold">{t.author}</p>
                      <p className="text-sm text-muted-foreground">{t.role}, {t.company}</p>
                      <p className="text-xs text-primary">{t.units}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Start free. Upgrade when you're ready. No hidden fees.
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${billingPeriod === 'annual' ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
              data-testid="billing-toggle"
              aria-label={`Switch to ${billingPeriod === 'monthly' ? 'annual' : 'monthly'} billing`}
              role="switch"
              aria-checked={billingPeriod === 'annual'}
            >
              <span 
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${billingPeriod === 'annual' ? 'translate-x-8' : 'translate-x-1'}`} 
              />
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium transition-colors ${billingPeriod === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>Annual</span>
              <span className="text-xs text-green-600 font-semibold bg-green-100 dark:bg-green-950/50 px-2 py-0.5 rounded-full">Save 17%</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {/* Free Plan */}
            <Card className="glass relative overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold font-heading">Free</h3>
                  <p className="text-muted-foreground">Perfect for getting started</p>
                </div>
                <div className="mb-4">
                  <span className="text-5xl font-bold font-heading">${pricingPlans[billingPeriod].free.price}</span>
                  <span className="text-muted-foreground">{pricingPlans[billingPeriod].free.period}</span>
                </div>
                <p className="text-sm text-green-600 font-medium mb-6 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Free forever, not a trial
                </p>
                <ul className="space-y-3 mb-8">
                  {planFeatures.free.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block">
                  <Button className="w-full" size="lg" variant="outline" data-testid="pricing-free-btn">
                    Start Free
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Standard Plan */}
            <Card className="glass relative overflow-hidden border-primary border-2">
              <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center text-sm font-medium py-1.5">
                MOST POPULAR
              </div>
              <CardContent className="p-8 pt-12">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold font-heading">Standard</h3>
                  <p className="text-muted-foreground">For growing portfolios</p>
                </div>
                <div className="mb-2">
                  <span className="text-5xl font-bold font-heading">${pricingPlans[billingPeriod].standard.price}</span>
                  <span className="text-muted-foreground">{pricingPlans[billingPeriod].standard.period}</span>
                </div>
                {pricingPlans[billingPeriod].standard.savings && (
                  <p className="text-sm text-green-600 font-medium mb-4">
                    {pricingPlans[billingPeriod].standard.savings}
                  </p>
                )}
                <ul className="space-y-3 mb-8">
                  {planFeatures.standard.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block">
                  <Button className="w-full btn-active" size="lg" data-testid="pricing-standard-btn">
                    Start Free, Upgrade Later
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className="glass relative overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold font-heading flex items-center gap-2">
                    Pro
                    <span className="text-xs bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full">Best Value</span>
                  </h3>
                  <p className="text-muted-foreground">For professional managers</p>
                </div>
                <div className="mb-2">
                  <span className="text-5xl font-bold font-heading">${pricingPlans[billingPeriod].pro.price}</span>
                  <span className="text-muted-foreground">{pricingPlans[billingPeriod].pro.period}</span>
                </div>
                {pricingPlans[billingPeriod].pro.savings && (
                  <p className="text-sm text-green-600 font-medium mb-4">
                    {pricingPlans[billingPeriod].pro.savings}
                  </p>
                )}
                <ul className="space-y-3 mb-8">
                  {planFeatures.pro.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block">
                  <Button className="w-full" size="lg" variant="outline" data-testid="pricing-pro-btn">
                    Start Free, Upgrade Later
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Enterprise Plan */}
            <Card className="glass relative overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold font-heading">Enterprise</h3>
                  <p className="text-muted-foreground">For multi-organization operators</p>
                </div>
                <div className="mb-2">
                  <span className="text-5xl font-bold font-heading">${pricingPlans[billingPeriod].enterprise.price}</span>
                  <span className="text-muted-foreground">{pricingPlans[billingPeriod].enterprise.period}</span>
                </div>
                {pricingPlans[billingPeriod].enterprise.savings && (
                  <p className="text-sm text-green-600 font-medium mb-4">
                    {pricingPlans[billingPeriod].enterprise.savings}
                  </p>
                )}
                <ul className="space-y-3 mb-8">
                  {planFeatures.enterprise.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <a href="mailto:sales@mypropops.com" className="block">
                  <Button className="w-full" size="lg" variant="outline" data-testid="pricing-enterprise-btn">
                    Contact Sales
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading">
              Frequently asked questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <Card key={i} className="glass">
                <CardContent className="p-0">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full p-6 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-6 pt-0">
                      <p className="text-muted-foreground">{faq.a}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-12 border border-primary/20">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading mb-4">
              Ready to take control of your properties?
            </h2>
            <p className="text-lg text-muted-foreground mb-4 max-w-xl mx-auto">
              Stop drowning in spreadsheets. Start with MyPropOps.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              Free forever for up to 2 properties • No credit card required • Setup in 15 minutes
            </p>
            <Link to="/register">
              <Button size="lg" className="btn-active text-base px-8" data-testid="cta-get-started">
                Start My Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground">
              Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="py-12 pb-24 md:pb-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/logo-small.jpg" 
                  alt="MyPropOps"
                  width="32"
                  height="32"
                  className="w-8 h-8 rounded-lg object-contain"
                />
                <span className="font-semibold">MyPropOps</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Simple property management for modern teams.
              </p>
            </div>
            <div>
              <p className="font-semibold mb-4">Product</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground">Features</a></li>
                <li><a href="#pricing" className="hover:text-foreground">Pricing</a></li>
                <li><a href="#testimonials" className="hover:text-foreground">Reviews</a></li>
                <li><a href="#faq" className="hover:text-foreground">FAQ</a></li>
                <li><Link to="/help" className="hover:text-foreground">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-4">Company</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-foreground">About</Link></li>
                <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
                <li><a href="mailto:support@mypropops.com" className="hover:text-foreground">Contact</a></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-4">Legal</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-foreground">Terms of Service</Link></li>
                <li><Link to="/dpa" className="hover:text-foreground">Data Processing Agreement</Link></li>
                <li><Link to="/legal/subprocessors" className="hover:text-foreground">Sub-Processors</Link></li>
                <li><Link to="/security" className="hover:text-foreground">Security</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} MyPropOps. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>Built by <a href="https://jonomor.com/ali-morgan" className="hover:text-foreground underline" target="_blank" rel="noopener noreferrer">Ali Morgan</a></span>
              <span className="hidden sm:inline">•</span>
              <span>Part of the <a href="https://jonomor.com/ecosystem" className="hover:text-foreground underline" target="_blank" rel="noopener noreferrer">Jonomor Ecosystem</a></span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
